/**
 * GitHub Data Service — calls GitHub REST API directly using a PAT.
 * The PAT is stored in secureStorage (sessionStorage, base64 obfuscated).
 * GitHub config (org, repos, enabled) is stored in localStorage.
 *
 * This is the "Option A" approach: direct API calls from the browser.
 * When a backend MCP proxy is ready, swap the base URL and auth header.
 */

import { secureStorage, KEYS } from './secureStorage'
const GITHUB_API = 'https://api.github.com'
const GITHUB_CONFIG_KEY = 'vsm_github_config'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GitHubConfig {
  enabled: boolean
  org: string
  /** Mapping: projectKey → repo slugs (e.g. { "PIAE": ["piae-backend", "piae-frontend"] }) */
  repoMappings: Record<string, string[]>
  /** Fallback: default repos when no mapping exists */
  defaultRepos: string[]
}
export interface GitHubPR {
  id: number
  number: number
  title: string
  state: 'open' | 'closed'
  draft: boolean
  merged: boolean
  user: {
    login: string
    avatar_url: string
  }
  created_at: string
  updated_at: string
  html_url: string
  head: {
    ref: string
  }
  base: {
    ref: string
  }
  additions: number
  deletions: number
  changed_files: number
  reviewDecision?: string
  labels: {
    name: string
    color: string
  }[]
  /** Extracted Jira key from title/branch (e.g. "PIAE-123") */
  jiraKey?: string
  /** Repository this PR belongs to */
  repoName: string
}
export interface GitHubCommit {
  sha: string
  message: string
  author: {
    login: string
    avatar_url: string
    date: string
  }
  html_url: string
  /** Extracted Jira key from commit message */
  jiraKey?: string
  /** Repository this commit belongs to */
  repoName: string
}
export interface GitHubBranch {
  name: string
  protected: boolean
  commit: {
    sha: string
    url: string
  }
  /** Extracted Jira key from branch name */
  jiraKey?: string
  /** Repository this branch belongs to */
  repoName: string
}
export interface GitHubWorkflowRun {
  id: number
  name: string
  status: 'completed' | 'in_progress' | 'queued' | 'waiting'
  conclusion: 'success' | 'failure' | 'cancelled' | 'skipped' | null
  html_url: string
  run_started_at: string
  updated_at: string
  head_branch: string
  head_sha: string
  /** Duration in seconds */
  duration?: number
  /** Repository this run belongs to */
  repoName: string
}
export interface GitHubPRFile {
  sha: string
  filename: string
  status:
    | 'added'
    | 'removed'
    | 'modified'
    | 'renamed'
    | 'copied'
    | 'changed'
    | 'unchanged'
  additions: number
  deletions: number
  changes: number
  blob_url: string
  raw_url: string
  contents_url: string
  patch?: string
}
export interface GitHubReview {
  id: number
  user: {
    login: string
    avatar_url: string
  }
  body: string
  state:
    | 'APPROVED'
    | 'CHANGES_REQUESTED'
    | 'COMMENTED'
    | 'DISMISSED'
    | 'PENDING'
  submitted_at: string
}
export interface GitHubReviewComment {
  id: number
  pull_request_review_id: number
  diff_hunk: string
  path: string
  position: number
  original_position: number
  commit_id: string
  original_commit_id: string
  user: {
    login: string
    avatar_url: string
  }
  body: string
  created_at: string
  updated_at: string
}
export interface GitHubRepo {
  id: number
  name: string
  full_name: string
  description: string | null
  html_url: string
  language: string | null
  stargazers_count: number
  open_issues_count: number
  default_branch: string
  updated_at: string
  private: boolean
  /** Permissions the token has on this repo */
  permissions?: {
    admin: boolean
    push: boolean
    pull: boolean
  }
}
export interface GitHubRateLimit {
  limit: number
  remaining: number
  reset: number
}

// ─── Config Management ────────────────────────────────────────────────────────

export function getGitHubConfig(): GitHubConfig {
  try {
    const raw = localStorage.getItem(GITHUB_CONFIG_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return {
    enabled: false,
    org: '',
    repoMappings: {},
    defaultRepos: [],
  }
}
export function saveGitHubConfig(config: GitHubConfig) {
  try {
    localStorage.setItem(GITHUB_CONFIG_KEY, JSON.stringify(config))
  } catch {}
}
export function isGitHubEnabled(): boolean {
  const config = getGitHubConfig()
  return config.enabled && !!secureStorage.get(KEYS.GITHUB_TOKEN)
}

// ─── Auto-seed credentials ────────────────────────────────────────────────────
// Seed GitHub PAT and config on module load so the integration works immediately

const DEFAULT_TOKEN = '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'

/**
 * Apply GitHub config for a specific user.
 * Called on module load (reads active user) and on login (with fresh profile).
 */
export function applyUserGitHubConfig(userGithub?: {
  org: string
  token?: string
}) {
  try {
    const token = userGithub?.token || DEFAULT_TOKEN
    const org = userGithub?.org || ''

    // Seed token into secure storage
    secureStorage.set(KEYS.GITHUB_TOKEN, token)
    if (!org) {
      console.log('[GitHubData] No GitHub org configured for user')
      return
    }

    // Check if org changed — if so, update config and clear cache
    const existing = getGitHubConfig()
    if (!existing.enabled || existing.org !== org) {
      const updated: GitHubConfig = {
        enabled: true,
        org,
        repoMappings: existing.repoMappings || {},
        defaultRepos: [], // Will be discovered via listRepos + verifyAccess
      }
      saveGitHubConfig(updated)
      // Clear verified repos cache when org changes
      try {
        sessionStorage.removeItem('vsm_github_verified_repos')
      } catch {}
      console.log('[GitHubData] Applied GitHub config for org:', org)
    } else {
      console.log('[GitHubData] GitHub config already set for org:', org)
    }
  } catch (err) {
    console.warn('[GitHubData] applyUserGitHubConfig failed:', err)
  }
}

// On module load, try to read active user's GitHub config
function ensureGitHubCredentials() {
  try {
    // Try to read active user from localStorage
    const activeEmail = localStorage.getItem('vsm_active_user')
    if (activeEmail) {
      const profilesRaw = localStorage.getItem('vsm_user_profiles')
      if (profilesRaw) {
        const profiles = JSON.parse(profilesRaw)
        const user = profiles.find(
          (p: any) => p.email?.toLowerCase() === activeEmail.toLowerCase(),
        )
        if (user?.github?.org) {
          applyUserGitHubConfig(user.github)
          return
        }
      }
    }

    // Fallback: check if config already has org+enabled (from previous session)
    // but token is missing (new window/tab). Re-seed the token.
    const existingConfig = getGitHubConfig()
    if (existingConfig.enabled && existingConfig.org) {
      secureStorage.set(KEYS.GITHUB_TOKEN, DEFAULT_TOKEN)
      console.log(
        '[GitHubData] Re-seeded token for existing config (new window), org:',
        existingConfig.org,
      )
      return
    }

    // Last fallback: just seed the token
    secureStorage.set(KEYS.GITHUB_TOKEN, DEFAULT_TOKEN)
    console.log('[GitHubData] Token seeded, waiting for user login to set org')
  } catch (err) {
    console.warn('[GitHubData] Auto-seed failed:', err)
  }
}
ensureGitHubCredentials()

// ─── Jira Key Extraction ──────────────────────────────────────────────────────

const JIRA_KEY_REGEX = /([A-Z][A-Z0-9]+-\d+)/
function extractJiraKey(text: string): string | undefined {
  const match = text.match(JIRA_KEY_REGEX)
  return match ? match[1] : undefined
}

// ─── API Helpers ──────────────────────────────────────────────────────────────

async function githubFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = secureStorage.get(KEYS.GITHUB_TOKEN)
  if (!token)
    throw new Error(
      'GitHub PAT no configurado. Configuralo en Settings → GitHub.',
    )
  const startTime = performance.now()
  const toolName = path.split('?')[0].replace(/^\//, '').replace(/\//g, '_')
  const response = await fetch(`${GITHUB_API}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
      ...options?.headers,
    },
  })
  const durationMs = Math.round(performance.now() - startTime)
  if (response.status === 401) {
    const err = 'GitHub PAT inválido o expirado. Verificalo en Settings.'
    logGitHub(toolName, path, null, false, err, durationMs)
    throw new Error(err)
  }
  if (response.status === 403) {
    const rateLimitRemaining = response.headers.get('X-RateLimit-Remaining')
    if (rateLimitRemaining === '0') {
      const resetTime = response.headers.get('X-RateLimit-Reset')
      const resetDate = resetTime
        ? new Date(parseInt(resetTime) * 1000).toLocaleTimeString()
        : 'pronto'
      const err = `Rate limit de GitHub alcanzado. Se renueva a las ${resetDate}.`
      logGitHub(toolName, path, null, false, err, durationMs)
      throw new Error(err)
    }
    const err = 'Acceso denegado. Verificá los permisos del PAT.'
    logGitHub(toolName, path, null, false, err, durationMs)
    throw new Error(err)
  }
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText)
    const err = `GitHub API error ${response.status}: ${errorText}`
    logGitHub(toolName, path, null, false, err, durationMs)
    throw new Error(err)
  }
  const data = await response.json()
  logGitHub(toolName, path, data, true, undefined, durationMs)
  return data
}

/** Log GitHub API calls to the Global Debug Console */
function logGitHub(
  tool: string,
  path: string,
  data: any,
  success: boolean,
  error?: string,
  durationMs?: number,
) {
  try {
    import('../hooks/useDebugLogs').then(({ pushDebugLog }) => {
      pushDebugLog({
        source: 'github',
        tool,
        params: {
          path,
        },
        rawResponse: data,
        processedData: data,
        success,
        error,
        durationMs,
      })
    })
  } catch {}
}

/** Get rate limit info from response headers */
async function getRateLimit(): Promise<GitHubRateLimit> {
  const token = secureStorage.get(KEYS.GITHUB_TOKEN)
  if (!token)
    return {
      limit: 0,
      remaining: 0,
      reset: 0,
    }
  const response = await fetch(`${GITHUB_API}/rate_limit`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/vnd.github+json',
    },
  })
  const data = await response.json()
  return (
    data.rate || {
      limit: 0,
      remaining: 0,
      reset: 0,
    }
  )
}

// ─── Repos for a project ──────────────────────────────────────────────────────

function getOwner(): string {
  return getGitHubConfig().org
}

/** Limit repos for detailed queries to avoid hammering the API */
const MAX_DETAILED_REPOS = 8
function getReposForProject(projectKey?: string): string[] {
  const config = getGitHubConfig()
  if (projectKey && config.repoMappings[projectKey]?.length) {
    return config.repoMappings[projectKey]
  }
  return config.defaultRepos.slice(0, MAX_DETAILED_REPOS)
}

// ─── Repo Access Verification Cache ───────────────────────────────────────────
const VERIFIED_REPOS_CACHE_KEY = 'vsm_github_verified_repos'
function getCachedVerifiedRepos(): string[] | null {
  try {
    const raw = sessionStorage.getItem(VERIFIED_REPOS_CACHE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      // Cache expires after 10 minutes
      if (parsed.timestamp && Date.now() - parsed.timestamp < 10 * 60 * 1000) {
        return parsed.repos
      }
    }
  } catch {}
  return null
}
function setCachedVerifiedRepos(repos: string[]) {
  try {
    sessionStorage.setItem(
      VERIFIED_REPOS_CACHE_KEY,
      JSON.stringify({
        repos,
        timestamp: Date.now(),
      }),
    )
  } catch {}
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const githubData = {
  /** Test connection — returns user info */
  async testConnection(): Promise<{
    login: string
    name: string
    avatar_url: string
  }> {
    return githubFetch('/user')
  },
  /** Get rate limit */
  getRateLimit,
  /** List org repos — includes permissions so callers can filter accessible ones */
  async listRepos(): Promise<GitHubRepo[]> {
    const owner = getOwner()
    if (!owner) return []
    try {
      return await githubFetch<GitHubRepo[]>(
        `/orgs/${owner}/repos?sort=updated&per_page=30`,
      )
    } catch {
      // Fallback to user repos if org fails
      return githubFetch<GitHubRepo[]>('/user/repos?sort=updated&per_page=30')
    }
  },
  /**
   * Verify which repos the token can actually access by probing each one.
   * Results are cached in sessionStorage for 10 minutes.
   * Uses /git/refs endpoint which requires actual content access (not just org visibility).
   */
  async verifyRepoAccess(
    repoNames: string[],
    projectKey?: string,
  ): Promise<string[]> {
    const config = getGitHubConfig()

    // If project has explicit mapping, trust it (user configured)
    if (projectKey && config.repoMappings[projectKey]?.length) {
      return config.repoMappings[projectKey]
    }

    // Check cache first
    const cached = getCachedVerifiedRepos()
    if (cached !== null) {
      console.log(
        `[GitHubData] Using cached verified repos: ${cached.length}`,
        cached,
      )
      return cached.slice(0, MAX_DETAILED_REPOS)
    }

    // Probe each repo with GET /repos/{owner}/{repo}/git/refs?per_page=1
    // This endpoint requires actual content read access, unlike HEAD /repos/{owner}/{repo}
    // which returns 200 for any repo visible in the org listing
    const owner = getOwner()
    const token = secureStorage.get(KEYS.GITHUB_TOKEN)
    if (!owner || !token) return []
    console.log(
      `[GitHubData] Verifying content access to ${repoNames.length} repos...`,
    )

    // Use Promise.allSettled for parallel probing
    const probes = repoNames.map(async (repo) => {
      try {
        const response = await fetch(
          `${GITHUB_API}/repos/${owner}/${repo}/git/refs?per_page=1`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/vnd.github+json',
              'X-GitHub-Api-Version': '2022-11-28',
            },
          },
        )
        return {
          repo,
          accessible: response.ok,
        }
      } catch {
        return {
          repo,
          accessible: false,
        }
      }
    })
    const results = await Promise.allSettled(probes)
    const verified: string[] = []
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value.accessible) {
        verified.push(result.value.repo)
      }
    }
    console.log(
      `[GitHubData] Verified content-accessible repos: ${verified.length}/${repoNames.length}`,
      verified.length > 0 ? verified : '(none — PAT may lack repo scope)',
    )

    // Cache the results (even if empty — avoids re-probing every render)
    setCachedVerifiedRepos(verified)

    // Return only verified repos — NO fallback to defaults
    // If 0 repos are accessible, the hook will skip detail fetches and show an error
    return verified.slice(0, MAX_DETAILED_REPOS)
  },
  /** List PRs — accepts explicit repoNames to avoid querying inaccessible repos */
  async listPRs(
    projectKey?: string,
    state: 'open' | 'closed' | 'all' = 'open',
    repoNames?: string[],
  ): Promise<GitHubPR[]> {
    const owner = getOwner()
    const repos = repoNames || getReposForProject(projectKey)
    if (!owner || repos.length === 0) return []
    const allPRs: GitHubPR[] = []
    for (const repo of repos) {
      try {
        const prs = await githubFetch<any[]>(
          `/repos/${owner}/${repo}/pulls?state=${state}&per_page=30&sort=updated`,
        )
        const mapped: GitHubPR[] = prs.map((pr) => ({
          id: pr.id,
          number: pr.number,
          title: pr.title,
          state: pr.state,
          draft: pr.draft || false,
          merged: pr.merged_at !== null,
          user: {
            login: pr.user.login,
            avatar_url: pr.user.avatar_url,
          },
          created_at: pr.created_at,
          updated_at: pr.updated_at,
          html_url: pr.html_url,
          head: {
            ref: pr.head.ref,
          },
          base: {
            ref: pr.base.ref,
          },
          additions: pr.additions || 0,
          deletions: pr.deletions || 0,
          changed_files: pr.changed_files || 0,
          labels: (pr.labels || []).map((l: any) => ({
            name: l.name,
            color: l.color,
          })),
          jiraKey: extractJiraKey(pr.title) || extractJiraKey(pr.head.ref),
          repoName: repo,
        }))
        allPRs.push(...mapped)
      } catch (err: any) {
        if (!err.message?.includes('404')) {
          console.warn(
            `[GitHubData] Failed to fetch PRs for ${owner}/${repo}:`,
            err,
          )
        }
      }
    }
    if (projectKey) {
      return allPRs.filter((pr) => pr.jiraKey?.startsWith(projectKey))
    }
    return allPRs
  },
  /** List recent commits — accepts explicit repoNames */
  async listCommits(
    projectKey?: string,
    perPage = 30,
    repoNames?: string[],
  ): Promise<GitHubCommit[]> {
    const owner = getOwner()
    const repos = repoNames || getReposForProject(projectKey)
    if (!owner || repos.length === 0) return []
    const allCommits: GitHubCommit[] = []
    for (const repo of repos) {
      try {
        const commits = await githubFetch<any[]>(
          `/repos/${owner}/${repo}/commits?per_page=${perPage}`,
        )
        const mapped: GitHubCommit[] = commits.map((c) => ({
          sha: c.sha,
          message: c.commit.message.split('\n')[0],
          author: {
            login: c.author?.login || c.commit.author.name,
            avatar_url: c.author?.avatar_url || '',
            date: c.commit.author.date,
          },
          html_url: c.html_url,
          jiraKey: extractJiraKey(c.commit.message),
          repoName: repo,
        }))
        allCommits.push(...mapped)
      } catch (err: any) {
        if (!err.message?.includes('404')) {
          console.warn(
            `[GitHubData] Failed to fetch commits for ${owner}/${repo}:`,
            err,
          )
        }
      }
    }
    allCommits.sort(
      (a, b) =>
        new Date(b.author.date).getTime() - new Date(a.author.date).getTime(),
    )
    if (projectKey) {
      return allCommits.filter((c) => c.jiraKey?.startsWith(projectKey))
    }
    return allCommits.slice(0, perPage)
  },
  /** List branches — accepts explicit repoNames */
  async listBranches(
    projectKey?: string,
    repoNames?: string[],
  ): Promise<GitHubBranch[]> {
    const owner = getOwner()
    const repos = repoNames || getReposForProject(projectKey)
    if (!owner || repos.length === 0) return []
    const allBranches: GitHubBranch[] = []
    for (const repo of repos) {
      try {
        const branches = await githubFetch<any[]>(
          `/repos/${owner}/${repo}/branches?per_page=50`,
        )
        const mapped: GitHubBranch[] = branches.map((b) => ({
          name: b.name,
          protected: b.protected,
          commit: {
            sha: b.commit.sha,
            url: b.commit.url,
          },
          jiraKey: extractJiraKey(b.name),
          repoName: repo,
        }))
        allBranches.push(...mapped)
      } catch (err: any) {
        if (!err.message?.includes('404')) {
          console.warn(
            `[GitHubData] Failed to fetch branches for ${owner}/${repo}:`,
            err,
          )
        }
      }
    }
    if (projectKey) {
      return allBranches.filter((b) => b.jiraKey?.startsWith(projectKey))
    }
    return allBranches
  },
  /** List workflow runs — accepts explicit repoNames */
  async listWorkflowRuns(
    projectKey?: string,
    perPage = 20,
    repoNames?: string[],
  ): Promise<GitHubWorkflowRun[]> {
    const owner = getOwner()
    const repos = repoNames || getReposForProject(projectKey)
    if (!owner || repos.length === 0) return []
    const allRuns: GitHubWorkflowRun[] = []
    for (const repo of repos) {
      try {
        const data = await githubFetch<{
          workflow_runs: any[]
        }>(`/repos/${owner}/${repo}/actions/runs?per_page=${perPage}`)
        const mapped: GitHubWorkflowRun[] = (data.workflow_runs || []).map(
          (r) => {
            const startedAt = new Date(r.run_started_at).getTime()
            const updatedAt = new Date(r.updated_at).getTime()
            return {
              id: r.id,
              name: r.name,
              status: r.status,
              conclusion: r.conclusion,
              html_url: r.html_url,
              run_started_at: r.run_started_at,
              updated_at: r.updated_at,
              head_branch: r.head_branch,
              head_sha: r.head_sha.slice(0, 7),
              duration:
                r.status === 'completed'
                  ? Math.round((updatedAt - startedAt) / 1000)
                  : undefined,
              repoName: repo,
            }
          },
        )
        allRuns.push(...mapped)
      } catch (err: any) {
        if (!err.message?.includes('404')) {
          console.warn(
            `[GitHubData] Failed to fetch workflow runs for ${owner}/${repo}:`,
            err,
          )
        }
      }
    }
    if (projectKey) {
      return allRuns.filter((r) => {
        const branchKey = extractJiraKey(r.head_branch)
        return branchKey?.startsWith(projectKey)
      })
    }
    return allRuns.slice(0, perPage)
  },
  async getPRFiles(
    repoName: string,
    prNumber: number,
  ): Promise<GitHubPRFile[]> {
    const owner = getOwner()
    return githubFetch<GitHubPRFile[]>(
      `/repos/${owner}/${repoName}/pulls/${prNumber}/files?per_page=100`,
    )
  },
  async getPRReviews(
    repoName: string,
    prNumber: number,
  ): Promise<GitHubReview[]> {
    const owner = getOwner()
    return githubFetch<GitHubReview[]>(
      `/repos/${owner}/${repoName}/pulls/${prNumber}/reviews?per_page=100`,
    )
  },
  async getPRComments(
    repoName: string,
    prNumber: number,
  ): Promise<GitHubReviewComment[]> {
    const owner = getOwner()
    return githubFetch<GitHubReviewComment[]>(
      `/repos/${owner}/${repoName}/pulls/${prNumber}/comments?per_page=100`,
    )
  },
  async submitPRReview(
    repoName: string,
    prNumber: number,
    event: 'APPROVE' | 'REQUEST_CHANGES' | 'COMMENT',
    body?: string,
  ): Promise<any> {
    const owner = getOwner()
    return githubFetch<any>(
      `/repos/${owner}/${repoName}/pulls/${prNumber}/reviews`,
      {
        method: 'POST',
        body: JSON.stringify({
          event,
          body,
        }),
      },
    )
  },
  async addPRComment(
    repoName: string,
    prNumber: number,
    body: string,
    commitId: string,
    path: string,
    position: number,
  ): Promise<any> {
    const owner = getOwner()
    return githubFetch<any>(
      `/repos/${owner}/${repoName}/pulls/${prNumber}/comments`,
      {
        method: 'POST',
        body: JSON.stringify({
          body,
          commit_id: commitId,
          path,
          position,
        }),
      },
    )
  },
  /** Get files changed in a specific commit (with diff patches) */
  async getCommitFiles(repoName: string, sha: string): Promise<GitHubPRFile[]> {
    const owner = getOwner()
    if (!owner) return []
    const data = await githubFetch<any>(
      `/repos/${owner}/${repoName}/commits/${sha}`,
    )
    return (data.files || []).map((f: any) => ({
      sha: f.sha,
      filename: f.filename,
      status: f.status,
      additions: f.additions,
      deletions: f.deletions,
      changes: f.changes,
      blob_url: f.blob_url,
      raw_url: f.raw_url,
      contents_url: f.contents_url || '',
      patch: f.patch,
    }))
  },
  async addPRGeneralComment(
    repoName: string,
    prNumber: number,
    body: string,
  ): Promise<any> {
    const owner = getOwner()
    return githubFetch<any>(
      `/repos/${owner}/${repoName}/issues/${prNumber}/comments`,
      {
        method: 'POST',
        body: JSON.stringify({
          body,
        }),
      },
    )
  },
  /** Get the last commit that touched the README file */
  async getReadmeLastCommit(repoName: string): Promise<{
    sha: string
    date: string
    message: string
    author: string
  } | null> {
    const owner = getOwner()
    if (!owner) return null
    try {
      const commits = await githubFetch<any[]>(
        `/repos/${owner}/${repoName}/commits?path=README.md&per_page=1`,
      )
      if (commits.length > 0) {
        const c = commits[0]
        return {
          sha: c.sha,
          date: c.commit.author.date,
          message: c.commit.message.split('\n')[0],
          author: c.author?.login || c.commit.author.name,
        }
      }
      return null
    } catch {
      return null
    }
  },
  /** Get README content for a repo (decoded from base64 with proper UTF-8 support) */
  async getReadme(repoName: string): Promise<{
    content: string
    name: string
  } | null> {
    const owner = getOwner()
    if (!owner) return null
    try {
      const data = await githubFetch<any>(`/repos/${owner}/${repoName}/readme`)
      if (data.content && data.encoding === 'base64') {
        // atob() returns a binary string (1 byte per char), but README content
        // is UTF-8 which uses multi-byte sequences for non-ASCII chars (ó, é, ñ, etc).
        // We must convert the binary string to a Uint8Array and decode with TextDecoder.
        const binaryString = atob(data.content.replace(/\n/g, ''))
        const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0))
        const decoded = new TextDecoder('utf-8').decode(bytes)
        return {
          content: decoded,
          name: data.name || 'README.md',
        }
      }
      return null
    } catch {
      return null
    }
  },
}
